/**
 * component-header.js
 * <header-root> and <mega-menu>.
 *
 * Two custom elements, no framework, no per-link listeners. Everything that
 * can be delegated is delegated, and everything that can be CSS is CSS — the
 * header runs on every page and is on the critical path for INP.
 */
(function () {
  'use strict';

  // Single-execution guard — see component-drawer.js. The outside-click
  // handler that closes mega menus is bound at document level.
  if (window.__themeHeaderLoaded) return;
  window.__themeHeaderLoaded = true;

  /* ==================================================================== *
   * <header-root>
   * Publishes the header's height, tracks scroll state, keeps the cart
   * count current.
   * ==================================================================== */

  class HeaderRoot extends HTMLElement {
    connectedCallback() {
      this.onScroll = window.Theme.throttleFrame(this.updateScrollState.bind(this));

      this.publishHeight();

      // The header's height changes with the logo setting, a wrapped nav, or
      // a font swap landing after first paint. Anything using
      // --header-height (scroll-padding, sticky offsets, the sticky
      // add-to-cart bar in F6) needs the real value, not a guess.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.publishHeight());
        this.resizeObserver.observe(this);
      }

      if (this.hasAttribute('data-sticky')) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateScrollState();
      }

      this.unsubscribeCart = window.Theme.on('cart:updated', (cart) => this.updateCartCount(cart));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.unsubscribeCart) this.unsubscribeCart();
    }

    publishHeight() {
      const height = Math.round(this.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--header-height', height + 'px');
      }
    }

    updateScrollState() {
      // A small threshold rather than > 0: sub-pixel scroll and iOS rubber
      // banding would otherwise toggle the class continuously.
      this.classList.toggle('is-scrolled', window.scrollY > 8);
    }

    /**
     * @param {{item_count: number}} cart Shopify cart JSON, published by F8.
     */
    updateCartCount(cart) {
      if (!cart || typeof cart.item_count !== 'number') return;

      const bubble = this.querySelector('[data-cart-count]');
      const label = this.querySelector('[data-cart-label]');

      if (bubble) {
        bubble.textContent = cart.item_count;
        bubble.hidden = cart.item_count === 0;
      }

      if (label) {
        // Both plural forms are rendered server-side so Liquid resolves the
        // pluralisation rules for the active locale. Substituting a number
        // into a single template would produce "1 items" in English and
        // worse in French.
        const template =
          cart.item_count === 1
            ? label.getAttribute('data-label-one')
            : label.getAttribute('data-label-other');

        if (template) label.textContent = template.replace(/\d+/, cart.item_count);
      }
    }
  }

  /* ==================================================================== *
   * <mega-menu>
   * One top-level nav item with a disclosure panel.
   * ==================================================================== */

  /** Currently open menu. Only one may be open at a time. */
  let openMenu = null;

  const HOVER_OPEN_DELAY = 90;
  const HOVER_CLOSE_DELAY = 220;

  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('[data-mega-toggle]');
      this.panel = this.querySelector('[data-mega-panel]');
      if (!this.toggleButton || !this.panel) return;

      this.hoverTimer = null;

      this.toggleButton.addEventListener('click', () => this.toggle());
      this.addEventListener('keydown', (event) => this.onKeydown(event));

      // Focus leaving the component closes it. `focusout` fires before the
      // new element receives focus, hence the relatedTarget check rather
      // than reading document.activeElement.
      this.addEventListener('focusout', (event) => {
        if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.close();
      });

      // Hover is an enhancement, and only for genuine hover pointers. On a
      // touch device `pointerenter` fires on tap and would open the panel at
      // the same moment the tap navigates the parent link.
      if (window.Theme.hasHover()) {
        this.addEventListener('pointerenter', () => this.scheduleOpen());
        this.addEventListener('pointerleave', () => this.scheduleClose());
      }
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    scheduleOpen() {
      clearTimeout(this.hoverTimer);
      // Intent delay: without it, sweeping the pointer across the nav to
      // reach the cart opens and closes every panel on the way.
      this.hoverTimer = setTimeout(() => this.open(), HOVER_OPEN_DELAY);
    }

    scheduleClose() {
      clearTimeout(this.hoverTimer);
      // Longer than the open delay so a diagonal path from the parent link
      // down into the panel does not close it mid-journey.
      this.hoverTimer = setTimeout(() => this.close(), HOVER_CLOSE_DELAY);
    }

    open() {
      if (this.isOpen) return;
      if (openMenu && openMenu !== this) openMenu.close();

      this.setAttribute('data-open', '');
      this.toggleButton.setAttribute('aria-expanded', 'true');
      openMenu = this;
    }

    close() {
      clearTimeout(this.hoverTimer);
      if (!this.isOpen) return;

      this.removeAttribute('data-open');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      if (openMenu === this) openMenu = null;
    }

    toggle() {
      clearTimeout(this.hoverTimer);
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      event.preventDefault();
      this.close();
      // Escape must return focus to the control that opened the panel, or
      // the keyboard user is left at the top of the document.
      this.toggleButton.focus();
    }
  }

  if (!customElements.get('header-root')) customElements.define('header-root', HeaderRoot);
  if (!customElements.get('mega-menu')) customElements.define('mega-menu', MegaMenu);

  /**
   * Clicking anywhere outside the open menu closes it. One document listener
   * rather than one per menu.
   */
  document.addEventListener('click', (event) => {
    if (!openMenu) return;
    if (!event.target.closest('mega-menu')) openMenu.close();
  });
})();
